import type { SingleValue } from '@ksyos/design-system';
import type { Dispatch, JSX, SetStateAction } from 'react';
import { Form, FormSection } from '@ksyos/design-system';
import type { ServiceOrganizationRole as Role } from '@common/enums';
import type { ServiceOrganizationRole } from '@common/interfaces/api/servicesApi';
import type { IOption } from '@frontend/components/interfaces';
import type { IServiceOrganizationSettingRoleDistribution } from '@frontend/components/interfaces/serviceOrganizationSettingRoleDistribution';
import { getSelectedLanguage } from '@frontend/translate';
import { useServiceOrganizationSettingContext } from '@frontend/utils/hooks/useServiceOrganizationsSettingsContext';
import type { ServiceOrganizationRoleDistributionsErrors } from './serviceOrganizationRoleDistributionTypes';
import { ServiceOrganizationRoleDistributionEdit } from './ServiceOrganizationRoleDistributionEdit';

interface IServiceOrganizationsRoleDistributionsEditProps {
    errorsByRole: Record<Role, ServiceOrganizationRoleDistributionsErrors>;
    isEquallyDistributedByRole: Record<Role, boolean>;
    roles: Role[];
    serviceOrganizationRoleDistributions: IServiceOrganizationSettingRoleDistribution[];
    serviceOrganizationRoles: ServiceOrganizationRole[];
    setErrorsByRole: Dispatch<SetStateAction<Record<Role, ServiceOrganizationRoleDistributionsErrors>>>;
    setIsEquallyDistributed: (role: Role, value: boolean) => void;
    setServiceOrganizationRoleDistributions: Dispatch<SetStateAction<IServiceOrganizationSettingRoleDistribution[]>>;
    showValidationAlerts: boolean;
    validateRoleDistributionsForRole: (items: IServiceOrganizationSettingRoleDistribution[], role: Role) => void;
}

export function ServiceOrganizationsRoleDistributionsEdit(
    props: IServiceOrganizationsRoleDistributionsEditProps
): JSX.Element {
    const { serviceOrganizationSetting } = useServiceOrganizationSettingContext();

    return (
        <Form submitOnEnter={false}>
            <FormSection>
                {props.roles.map((role) => (
                    <ServiceOrganizationRoleDistributionEdit
                        errors={props.errorsByRole[role]}
                        handleAddOrganization={(serviceOrganizationRole) =>
                            handleAddOrganization(role, serviceOrganizationRole)
                        }
                        handleChangeOrganization={(previousOrganizationId, selectedOption) =>
                            handleChangeOrganization(role, previousOrganizationId, selectedOption)
                        }
                        handleDeleteOrganization={(organizationId) => handleDeleteOrganization(organizationId, role)}
                        handleDistributionChange={handleDistributionChange}
                        handleEqualDistributionChange={() => handleEqualDistributionChange(role)}
                        isEquallyDistributed={props.isEquallyDistributedByRole[role]}
                        key={role}
                        label={getSelectedLanguage().serviceOrganizationRoleEnum[role]}
                        serviceOrganizationRoleDistributions={filterByRole(
                            role,
                            props.serviceOrganizationRoleDistributions
                        )}
                        serviceOrganizationRoles={filterByRole(role, props.serviceOrganizationRoles)}
                        setIsEquallyDistributed={(value: boolean) => props.setIsEquallyDistributed(role, value)}
                        showValidationAlerts={props.showValidationAlerts}
                    />
                ))}
            </FormSection>
        </Form>
    );

    function filterByRole<T extends { role: Role }>(role: Role, items: T[]): T[] {
        return items.filter((item) => item.role === role);
    }

    function handleDeleteOrganization(organizationId: string, role: Role): void {
        resetErrorsForRole(role);

        props.setServiceOrganizationRoleDistributions(
            props.serviceOrganizationRoleDistributions.filter(
                (serviceOrganizationDistributionRole) =>
                    !(
                        serviceOrganizationDistributionRole.serviceOrganizationRole.serviceOrganizationSetting
                            .organization.id === organizationId && serviceOrganizationDistributionRole.role === role
                    )
            )
        );
    }

    function handleChangeOrganization(
        role: Role,
        previousOrganizationId: string,
        selectedOption: SingleValue<IOption<string>>
    ): void {
        resetErrorsForRole(role);

        if (!selectedOption?.label || !selectedOption?.value) {
            return;
        }

        const newRoleId = props.serviceOrganizationRoles.find(
            (serviceOrganizationRoles) => serviceOrganizationRoles.id === selectedOption.value
        )?.id;

        if (!newRoleId) {
            return;
        }

        props.setServiceOrganizationRoleDistributions([
            ...props.serviceOrganizationRoleDistributions.filter(
                (serviceOrganizationDistributionRole) =>
                    !(
                        serviceOrganizationDistributionRole.serviceOrganizationRole.serviceOrganizationSetting
                            .organization.id === previousOrganizationId &&
                        serviceOrganizationDistributionRole.role === role
                    )
            ),
            {
                percentage: 0,
                role: role,
                serviceOrganizationRole: {
                    id: newRoleId,
                    serviceOrganizationSetting: {
                        organization: {
                            id: selectedOption.value,
                            name: selectedOption.label,
                        },
                    },
                },
                serviceOrganizationSetting,
            },
        ]);
    }

    function handleAddOrganization(role: Role, serviceOrganizationRole: ServiceOrganizationRole): void {
        resetErrorsForRole(role);

        props.setServiceOrganizationRoleDistributions([
            ...props.serviceOrganizationRoleDistributions,
            {
                percentage: 0,
                role: role,
                serviceOrganizationRole,
                serviceOrganizationSetting,
            },
        ]);
    }

    function handleEqualDistributionChange(role: Role): void {
        const isEquallyDistributed = !props.isEquallyDistributedByRole[role];
        props.setIsEquallyDistributed(role, isEquallyDistributed);
        resetErrorsForRole(role);
        const newPercentage = 100 / filterByRole(role, props.serviceOrganizationRoleDistributions).length;

        props.setServiceOrganizationRoleDistributions((prevDistributionRoles) =>
            prevDistributionRoles.map((distributionRole) => {
                if (distributionRole.role === role) {
                    return {
                        ...distributionRole,
                        percentage: isEquallyDistributed ? newPercentage : Math.round(newPercentage),
                    };
                }

                return distributionRole;
            })
        );
    }

    function handleDistributionChange(percentage: number | null, id: string): void {
        const index = props.serviceOrganizationRoleDistributions.findIndex(
            (item) => item.serviceOrganizationRole.id === id
        );
        const newRoleDistributions = [...props.serviceOrganizationRoleDistributions];
        newRoleDistributions[index].percentage = percentage ?? 0;

        props.setServiceOrganizationRoleDistributions(newRoleDistributions);

        props.validateRoleDistributionsForRole(newRoleDistributions, newRoleDistributions[index].role);
    }

    function resetErrorsForRole(role: Role) {
        props.setErrorsByRole({ ...props.errorsByRole, [role]: new Map() });
    }
}
